'use client'
import { type FC, useEffect, useMemo, useRef, useState } from 'react'
import { Box, Button, Checkbox, HStack, VStack } from '@chakra-ui/react'
import type { CompanyAddressDTO, CountryCode, OrderAddressDTO } from 'ecosystem'
import { getDocumentLanguage, resolveCountryName } from 'shared-utils'
import { GrandRadioCard, GrandText } from 'ui'
import type { RememberedAddressChoice } from '../../../hooks/useRememberedAddress'
import { CheckoutSectionHeading } from '../../CheckoutSectionHeading'
import {
  companyAddressToOrderAddress,
  findMatchingCompanyAddress
} from './companyAddressToOrderAddress'
import {
  CustomAddressForm,
  type CustomAddressFormLabels,
  type OrderAddressWithContact
} from './CustomAddressForm'

const CUSTOM = '__custom__'

const EMPTY_ADDRESS: OrderAddressDTO = {
  address1: '',
  address2: '',
  attention: '',
  country: '',
  mobilePhone: '',
  postalCode: '',
  postalRegion: '',
  reference: ''
}

export interface CompanyAddressPickerLabels extends CustomAddressFormLabels {
  heading: string
  foldedHint: string
  change: string
  customOption: string
  empty: string
}

interface CompanyAddressPickerProps {
  value: OrderAddressDTO | undefined
  onChange: (next: OrderAddressWithContact) => void
  /** Forwarded to {@link CustomAddressForm} — require contact person, phone and email. */
  requireContactDetails?: boolean
  /** Forwarded to {@link CustomAddressForm} — render the reference field. Default on. */
  showReference?: boolean
  /** Forwarded to {@link CustomAddressForm} — render the phone field. Default on. */
  showPhone?: boolean
  /** Forwarded to {@link CustomAddressForm} — preselect the country. */
  defaultCountry?: CountryCode
  /** Pre-filtered list (caller decides `BILLING` vs `SHIPPING`). */
  addresses: CompanyAddressDTO[]
  labels: CompanyAddressPickerLabels
  /**
   * Records which option the shopper has settled on, so it can be preselected next time.
   *
   * Derived from `value` rather than from the click that produced it: `value` only
   * changes at a commit point — a radio pick, or Save on the custom form — so deriving
   * keeps keystrokes out of device memory without threading a callback down every path.
   */
  onChoose?: (choice: RememberedAddressChoice<OrderAddressWithContact>) => void
  /** The "remember my choice on this device" checkbox. Rendered only when wired up. */
  rememberOnDevice?: { checked: boolean; onChange: (next: boolean) => void }
}

/**
 * Generic folded-card + radio picker + inline custom-form, shared by the
 * billing and shipping pickers. The parent supplies the address list
 * (already filtered by `type === 'BILLING' | 'SHIPPING'`) and locale-aware
 * labels.
 *
 * The custom-address form sits outside `GrandRadioCard.Item` on purpose:
 * `Item` wraps its children in `<Box as="label">`, which intercepts clicks
 * on the country select / text inputs and routes focus back to the radio
 * input. Rendering it as a sibling keeps the form's interactive elements
 * working normally.
 */
export const CompanyAddressPicker: FC<CompanyAddressPickerProps> = ({
  value,
  onChange,
  requireContactDetails,
  showReference,
  showPhone,
  defaultCountry,
  addresses,
  labels,
  onChoose,
  rememberOnDevice
}) => {
  // Start open when there's nothing selected yet so the shopper sees the
  // picker (or the custom-address form for B2C) right away — folding an
  // empty card hides a required step behind a "Change" button.
  const [mode, setMode] = useState<'folded' | 'picker'>(value ? 'folded' : 'picker')

  // Auto-fold the *first* time a value lands externally — covers B2B where
  // `companyProfile.addresses` hydrates after first paint and the parent
  // seeds the default SHIPPING address late. Once the shopper has
  // interacted (clicked Change → mode='picker'), this no-op's; user intent
  // wins over hydration.
  const seededRef = useRef(!!value)
  useEffect(() => {
    if (!seededRef.current && value) {
      seededRef.current = true
      setMode('folded')
    }
  }, [value])

  // Identify which company address (if any) currently matches `value` so the
  // picker can pre-select the right radio when the user enters it.
  const matchedAddressId = useMemo<string>(
    () => findMatchingCompanyAddress(value, addresses)?.id ?? CUSTOM,
    [addresses, value]
  )

  const [selectedId, setSelectedId] = useState<string>(matchedAddressId)

  const locale = getDocumentLanguage()
  const renderAddressLines = (a: OrderAddressDTO) =>
    [
      a.attention,
      a.address1,
      a.address2,
      [a.postalCode, a.postalRegion].filter(Boolean).join(' '),
      resolveCountryName(a.country, locale) ?? a.country
    ].filter(Boolean)

  // `value` is already the resolved address — the order, the company account, or what
  // this device remembered — so it is also the right seed for the custom form.
  const customFormDefaults = value ?? EMPTY_ADDRESS

  // The shopper's current pick, expressed as the thing worth remembering. A company
  // address is stored as its id so the account stays the source of truth for its
  // contents; anything else is the address they typed themselves.
  const choice = useMemo<RememberedAddressChoice<OrderAddressWithContact> | undefined>(() => {
    if (!value) return undefined
    return matchedAddressId === CUSTOM
      ? { source: 'custom', address: value }
      : { source: 'company', companyAddressId: matchedAddressId }
  }, [matchedAddressId, value])

  // One write path for both options — and it re-runs when `onChoose` changes identity as
  // the opt-in flips, so ticking the box commits what is already selected rather than
  // waiting for the shopper to pick something again.
  useEffect(() => {
    if (!choice) return
    onChoose?.(choice)
  }, [choice, onChoose])

  const handleRadioChange = (next: string) => {
    setSelectedId(next)
    if (next === CUSTOM) return
    const company = addresses.find((a) => a.id === next)
    if (!company) return
    onChange(companyAddressToOrderAddress(company))
    setMode('folded')
  }

  if (mode === 'folded') {
    return (
      <HStack align="flex-start" gap={4} justify="space-between">
        <VStack align="flex-start" gap={1}>
          <CheckoutSectionHeading>{labels.heading}</CheckoutSectionHeading>
          {value ? (
            <>
              <GrandText color="text.mild" fontSize="xs">
                {labels.foldedHint}
              </GrandText>
              {renderAddressLines(value).map((line) => (
                <GrandText fontSize="sm" key={line}>
                  {line}
                </GrandText>
              ))}
            </>
          ) : (
            <GrandText color="text.mild" fontSize="sm">
              {labels.empty}
            </GrandText>
          )}
        </VStack>
        <Button mt={2} onClick={() => setMode('picker')} size="sm" variant="ghost">
          {labels.change}
        </Button>
      </HStack>
    )
  }

  // With nothing saved to choose between, the radio is a single option the shopper
  // cannot deselect — noise in front of the only form they can fill in.
  const hasSavedAddresses = addresses.length > 0

  const customForm = (
    <Box borderColor="blue.500" borderRadius="md" borderWidth="1px" p={4}>
      <CustomAddressForm
        defaultCountry={defaultCountry}
        requireContactDetails={requireContactDetails}
        showReference={showReference}
        showPhone={showPhone}
        defaultValues={customFormDefaults}
        labels={labels}
        rememberOnDevice={rememberOnDevice}
        onCancel={() => setMode('folded')}
        onSubmit={(next) => {
          onChange(next)
          setMode('folded')
        }}
      />
    </Box>
  )

  if (!hasSavedAddresses) {
    return (
      <VStack align="stretch" gap={4}>
        <CheckoutSectionHeading>{labels.heading}</CheckoutSectionHeading>
        {customForm}
      </VStack>
    )
  }

  return (
    <VStack align="stretch" gap={4}>
      <CheckoutSectionHeading>{labels.heading}</CheckoutSectionHeading>

      <GrandRadioCard.Root
        onChange={handleRadioChange}
        size="md"
        value={selectedId}
        variant="outline"
      >
        <VStack align="stretch" gap={3}>
          {addresses.map((a) => {
            const mapped = companyAddressToOrderAddress(a)
            return (
              <GrandRadioCard.Item key={a.id} value={a.id}>
                <GrandRadioCard.ItemHeader>
                  <GrandRadioCard.ItemContent>
                    <GrandRadioCard.ItemText>
                      {a.label ?? mapped.attention ?? mapped.address1}
                    </GrandRadioCard.ItemText>
                    {renderAddressLines(mapped).map((line) => (
                      <GrandRadioCard.ItemDescription key={line}>
                        {line}
                      </GrandRadioCard.ItemDescription>
                    ))}
                  </GrandRadioCard.ItemContent>
                </GrandRadioCard.ItemHeader>
              </GrandRadioCard.Item>
            )
          })}

          <GrandRadioCard.Item value={CUSTOM}>
            <GrandRadioCard.ItemHeader>
              <GrandRadioCard.ItemContent>
                <GrandRadioCard.ItemText>{labels.customOption}</GrandRadioCard.ItemText>
              </GrandRadioCard.ItemContent>
            </GrandRadioCard.ItemHeader>
          </GrandRadioCard.Item>
        </VStack>
      </GrandRadioCard.Root>

      {selectedId === CUSTOM ? (
        // The custom form carries its own copy of the checkbox, above its Save button.
        customForm
      ) : (
        <>
          {rememberOnDevice ? (
            <Checkbox
              borderColor="primary"
              isChecked={rememberOnDevice.checked}
              onChange={(event) => rememberOnDevice.onChange(event.target.checked)}
              size="md"
            >
              {labels.rememberOnDevice}
            </Checkbox>
          ) : null}

          <HStack justify="flex-end">
            <Button onClick={() => setMode('folded')} size="sm" variant="ghost">
              {labels.cancel}
            </Button>
          </HStack>
        </>
      )}
    </VStack>
  )
}
